import { Outlet, Link, useLocation } from 'react-router-dom'
import { useAuth } from '@/lib/auth'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import CrisisIndicator from './CrisisIndicator'
import LanguageToggle from './LanguageToggle'

export default function Layout() {
  const location = useLocation()
  const { user, isAuthenticated, logout } = useAuth()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const { t } = useTranslation('common')

  const navigation = [
    { name: t('nav.home'), href: '/', public: true },
    // { name: t('nav.letters'), href: '/letters', public: false }, // COMMENTED OUT FOR NOW
    { name: t('nav.upload'), href: '/upload', public: false },
    { name: t('nav.docs'), href: '/docs', public: true },
  ]

  const handleLogout = async () => {
    await logout()
  }

  return (
    <div className="min-h-screen bg-stone-50">
      <nav className="bg-white/95 backdrop-blur-sm shadow-sm border-b border-olive-100/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Left side - Logo and Navigation */}
            <div className="flex items-center">
              <Link to="/" className="flex items-center hover:opacity-90 transition-opacity">
                <span className="text-2xl font-bold text-olive-700">KeffiyehQL</span>
              </Link>

              <div className="hidden md:flex md:ml-10 md:rtl:mr-10 md:rtl:ml-0 md:space-x-8 md:rtl:space-x-reverse">
                {navigation.map((item) => {
                  if (!item.public && !isAuthenticated) return null
                  const isActive = location.pathname === item.href
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`relative inline-flex items-center text-sm font-medium transition-colors ${
                        isActive ? 'text-olive-900' : 'text-olive-600 hover:text-olive-900'
                      }`}
                    >
                      {item.name}
                      {isActive && (
                        <span className="absolute inset-x-0 -bottom-[17px] h-0.5 bg-olive-600 rounded-full" />
                      )}
                    </Link>
                  )
                })}
              </div>
            </div>

            {/* Right side - Actions */}
            <div className="flex items-center gap-3">
              {/* Desktop menu items */}
              <div className="hidden md:flex md:items-center md:gap-3">
                <CrisisIndicator />
                <LanguageToggle />

                {isAuthenticated ? (
                  <>
                    <Link
                      to="/dashboard"
                      className="px-3 py-1.5 text-sm font-medium text-olive-700 hover:text-olive-900 transition-colors"
                    >
                      {user?.username}
                    </Link>
                    <Link
                      to="/settings"
                      className="p-1.5 text-olive-600 hover:text-olive-900 hover:bg-olive-50 rounded-lg transition-colors"
                      title={t('nav.settings')}
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="px-4 py-1.5 text-sm font-medium bg-olive-100 text-olive-700 rounded-full hover:bg-olive-200 transition-colors"
                    >
                      {t('nav.logout')}
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      to="/login"
                      className="px-3 py-1.5 text-sm font-medium text-olive-700 hover:text-olive-900 transition-colors"
                    >
                      {t('nav.login')}
                    </Link>
                    <Link
                      to="/signup"
                      className="px-4 py-1.5 text-sm font-medium bg-olive-600 text-white rounded-full hover:bg-olive-700 transition-colors"
                    >
                      {t('nav.signup')}
                    </Link>
                  </>
                )}
              </div>

              {/* Mobile menu button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 rounded-lg text-olive-600 hover:bg-olive-50 transition-colors"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  {mobileMenuOpen ? (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  ) : (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  )}
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-olive-100/50">
            <div className="px-4 py-3 space-y-1">
              {navigation.map((item) => {
                if (!item.public && !isAuthenticated) return null
                const isActive = location.pathname === item.href
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`block px-3 py-2 text-base font-medium rounded-lg transition-colors ${
                      isActive
                        ? 'bg-olive-100 text-olive-900'
                        : 'text-olive-600 hover:bg-olive-50 hover:text-olive-900'
                    }`}
                  >
                    {item.name}
                  </Link>
                )
              })}

              <div className="pt-3 mt-3 border-t border-olive-100 space-y-2">
                <div className="flex items-center justify-between px-3 py-1">
                  <span className="text-sm text-olive-600">Language</span>
                  <LanguageToggle />
                </div>
                <CrisisIndicator />

                {isAuthenticated ? (
                  <>
                    <Link
                      to="/dashboard"
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-3 py-2 text-base font-medium text-olive-700 hover:bg-olive-50 rounded-lg transition-colors"
                    >
                      {user?.username}
                    </Link>
                    <Link
                      to="/settings"
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-3 py-2 text-base font-medium text-olive-700 hover:bg-olive-50 rounded-lg transition-colors"
                    >
                      {t('nav.settings')}
                    </Link>
                    <button
                      onClick={() => {
                        handleLogout()
                        setMobileMenuOpen(false)
                      }}
                      className="w-full text-start px-3 py-2 text-base font-medium text-olive-700 hover:bg-olive-50 rounded-lg transition-colors"
                    >
                      {t('nav.logout')}
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      to="/login"
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-3 py-2 text-base font-medium text-olive-700 hover:bg-olive-50 rounded-lg transition-colors"
                    >
                      {t('nav.login')}
                    </Link>
                    <Link
                      to="/signup"
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-3 py-2 text-base font-medium bg-olive-600 text-white rounded-lg hover:bg-olive-700 transition-colors text-center"
                    >
                      {t('nav.signup')}
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>

      <main>
        <Outlet />
      </main>

      <footer className="bg-white border-t border-olive-100 mt-16">
        <div className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
          <div className="text-center text-sm text-olive-600">
            <p>
              {t('app.name')} - {t('app.tagline')}
            </p>
            <p className="mt-2">{t('footer.builtFor')}</p>
          </div>
        </div>
      </footer>
    </div>
  )
}
